package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.event.DragEvent
import top.wcpe.allincore.ui.runtime.event.ScrollEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmName
import kotlin.math.abs

/**
 * 滚动容器抽象基类：统一收口"内容超出视窗时可滚动"这一族容器的共有状态与行为。
 *
 * 共有职责（子类不再各自重复实现）：
 * - 滚动状态：scrollX / scrollY（二维统一，垂直容器水平偏移恒为 0）；
 * - 视窗尺寸：viewportWidth / viewportHeight，由渲染端按帧写入；
 * - 内容尺寸：contentWidth / contentHeight，全量容器由渲染端测量、虚拟容器由子类按数据量计算（覆盖 getter）；
 * - 钳制算法：[clampX] / [clampY] 依据 [direction] 锁轴并限制到 [0, max]；
 * - 事件接管：构造时默认安装 onScroll/onDrag，滚轮按 SCROLL_STEP 累加、拖拽 thumb-style 直接累加；
 *   [onScroll] / [onDrag] 重写为"先跑默认滚动逻辑，再回调用户 handler"；
 * - 子内容偏移：[contentOffsetX] / [contentOffsetY] 供 UIRenderer 与命中测试统一计算子节点绘制/命中原点。
 *
 * 渲染与命中：UIRenderer 对所有 ScrollableContainer 统一走 "背景 → pushClip → 子节点按 contentOffset 偏移
 * 绘制 → 滚动条" 路径，不再对单一子类做 instanceof 特化。
 *
 * @param SELF 自类型，便于链式 API 返回具体子类型
 */
@Suppress("TooManyFunctions", "MagicNumber")
abstract class ScrollableContainer<SELF : ScrollableContainer<SELF>> protected constructor() :
    UIElement<SELF>(),
    FrameTickable {

    /**
     * 滚动容器不摊平透明（$for/$cond）子：见 LayoutNode.flattensTransparentChildren()。
     * 摊平会让循环项绕过 ScrollView.child(UIElement) 的 flexShrink=0 保护而被压进视窗、无法滚动；
     * 故保持透明容器为受保护的直接子（justify 在滚动容器本无意义，无损失）。
     */
    override fun flattensTransparentChildren(): Boolean = false

    /** 滚轮一次刻度的像素步长；null = 回退默认 [SCROLL_STEP]（20f）。 */
    private var mouseWheelStepField: Float? = null

    private var directionField: ScrollDirection = ScrollDirection.VERTICAL

    private var scrollXField: Float = 0f
    private var scrollYField: Float = 0f
    private var viewportWidthField: Float = 0f
    private var viewportHeightField: Float = 0f
    private var contentWidthField: Float = 0f
    private var contentHeightField: Float = 0f

    // -------- 滚动状态双向绑定（特性①:scrollY/scrollX = state.x） --------
    /** scrollY 绑定的 state 信号;非空时滚动变化回写、信号变化驱动滚动。 */
    private var scrollYBinding: Signal<Any?>? = null

    /** scrollX 绑定的 state 信号。 */
    private var scrollXBinding: Signal<Any?>? = null

    /** 防回写环:正在把信号值写入元素时,onScrollStateChanged 不再反向写信号。 */
    private var applyingBoundScroll = false

    // -------- 滚动条样式（B 类 14） --------
    private var scrollbarModeField: ScrollbarMode = ScrollbarMode.AUTO

    /** 滚动条 thumb 颜色；0 表示沿用主题 outline。 */
    private var scrollbarColorField = 0

    /** 滚动条 track 颜色；0 表示沿用主题 outlineVariant。 */
    private var scrollbarTrackColorField = 0

    /** 滚动条粗细（像素）。 */
    private var scrollbarWidthField = 3f

    // -------- 弹性边缘（B 类 23） --------
    private var bounceField = false
    private var bounceMaxDistanceField = 30f

    // -------- 嵌套滚动（D 类 4，Phase 4） --------
    /** 是否参与嵌套滚动冒泡：true 时到边界后把剩余滚动量交给父容器，false 时吃掉不冒泡。 */
    private var nestedScrollField = true

    // -------- 惯性（B 类 25） --------
    private var inertiaField = true
    private var inertiaDecayField = 0.95f

    // -------- 平滑滚动（B 类 26） --------
    private var smoothScrollField = true
    private var smoothScrollDurationMsField = 200

    /** 帧驱动的滚动动画器（inertia / smoothScroll / bounce-back）。 */
    private val scrollAnimator = ScrollAnimator()

    /** 拖拽速度估算：上一次 drag 的位移与时刻，用于 onDragEnd 时计算惯性初速度。 */
    private var lastDragVelX = 0f
    private var lastDragVelY = 0f
    private var lastDragMs = -1L
    private var dragging = false

    /** scrollIntoView 的延迟请求：渲染端在下一帧拿到 LayoutResult 后兑现。 */
    private var pendingScrollIntoViewIdField: String? = null
    private var pendingScrollIntoViewAlignField: ViewAlign = ViewAlign.NEAREST

    /** 滚动条显示策略。 */
    enum class ScrollbarMode {
        /** 内容溢出时显示。 */
        AUTO,

        /** 始终显示。 */
        ALWAYS,

        /** 从不显示。 */
        NEVER,
    }

    /** scrollIntoView 对齐方式。 */
    enum class ViewAlign {
        START,
        CENTER,
        END,
        NEAREST,
    }

    /** 滚动位置快照,供 DSL/业务读取进度。 */
    data class ScrollPosition(
        @get:JvmName("x") val x: Float,
        @get:JvmName("y") val y: Float,
        @get:JvmName("maxX") val maxX: Float,
        @get:JvmName("maxY") val maxY: Float,
        @get:JvmName("progressX") val progressX: Float,
        @get:JvmName("progressY") val progressY: Float,
    )

    init {
        // 默认接管 onScroll/onDrag：按 direction 把滚轮/拖拽位移映射到对应轴。
        // 约定：滚轮 sy>0（向上滚）内容向下移 → scrollY 减小；拖拽 dy>0（向下拖）→ scrollY 增大。
        super.onScroll(UiEventHandler { applyScrollEvent(it) })
        super.onDrag(UiEventHandler { applyDragEvent(it) })
    }

    // -------- 滚动方向 --------

    open fun direction(d: ScrollDirection): SELF {
        directionField = d
        scrollXField = clampX(scrollXField)
        scrollYField = clampY(scrollYField)
        onScrollStateChanged()
        return self()
    }

    fun direction(): ScrollDirection = directionField

    // -------- 滚动偏移 --------

    fun scrollX(x: Float): SELF {
        val old = scrollXField
        scrollXField = clampX(x)
        if (scrollXField != old) {
            onScrollStateChanged()
        }
        return self()
    }

    fun scrollX(): Float = scrollXField

    fun scrollY(y: Float): SELF {
        val old = scrollYField
        scrollYField = clampY(y)
        if (scrollYField != old) {
            onScrollStateChanged()
        }
        return self()
    }

    fun scrollY(): Float = scrollYField

    // -------- 视窗尺寸（渲染端按帧写入） --------

    open fun viewportWidth(w: Float): SELF {
        viewportWidthField = w.coerceAtLeast(0f)
        scrollXField = clampX(scrollXField)
        onScrollStateChanged()
        return self()
    }

    fun viewportWidth(): Float = viewportWidthField

    open fun viewportHeight(h: Float): SELF {
        viewportHeightField = h.coerceAtLeast(0f)
        scrollYField = clampY(scrollYField)
        onScrollStateChanged()
        return self()
    }

    fun viewportHeight(): Float = viewportHeightField

    // -------- 内容尺寸 --------

    fun contentWidth(w: Float): SELF {
        contentWidthField = w.coerceAtLeast(0f)
        scrollXField = clampX(scrollXField)
        return self()
    }

    open fun contentWidth(): Float = contentWidthField

    fun contentHeight(h: Float): SELF {
        contentHeightField = h.coerceAtLeast(0f)
        scrollYField = clampY(scrollYField)
        return self()
    }

    open fun contentHeight(): Float = contentHeightField

    // -------- 子内容偏移（渲染 / 命中统一口径） --------

    /**
     * 子节点绘制/命中的水平原点偏移：默认 `-scrollX`（内容随滚动左移）。
     * 虚拟容器（窗口子节点 0 基排布）覆盖此方法叠加"窗口首项的内容坐标"。
     */
    open fun contentOffsetX(): Float = -scrollXField

    /** 子节点绘制/命中的垂直原点偏移：默认 `-scrollY`。 */
    open fun contentOffsetY(): Float = -scrollYField

    /**
     * 渲染端是否需要遍历子节点测量内容尺寸。
     * 全量容器（ScrollView）返回 `true`；虚拟容器按数据量计算内容尺寸，返回 `false`。
     */
    open fun requiresContentMeasure(): Boolean = true

    // -------- 钩子 --------

    /**
     * 滚动状态（scrollX/Y、viewport、direction）变化时的钩子。
     * 默认回写绑定信号；虚拟容器覆盖此方法触发窗口重算（并 super 调用本实现）。
     */
    protected open fun onScrollStateChanged() {
        writeBackScrollBindings()
    }

    /**
     * 把当前 scrollX/scrollY 回写到绑定的 state 信号(特性①:元素→state 方向)。
     * 仅在非"信号驱动写入"期间执行,避免 信号→元素→信号 死循环。值未变时不写,避免冗余通知。
     */
    private fun writeBackScrollBindings() {
        if (applyingBoundScroll) {
            return
        }
        val yBinding = scrollYBinding
        if (yBinding != null) {
            val cur = yBinding.get()
            val curD = if (cur is Number) cur.toDouble() else Double.NaN
            if (curD != scrollYField.toDouble()) {
                yBinding.set(scrollYField.toDouble())
            }
        }
        val xBinding = scrollXBinding
        if (xBinding != null) {
            val cur = xBinding.get()
            val curD = if (cur is Number) cur.toDouble() else Double.NaN
            if (curD != scrollXField.toDouble()) {
                xBinding.set(scrollXField.toDouble())
            }
        }
    }

    /**
     * 绑定 scrollY 到 state 信号(双向):
     * - state→元素:注册 Effect,信号变化时把值写入 scrollY(标记 applyingBoundScroll 防环);
     * - 元素→state:滚动变化经 [onScrollStateChanged] 回写信号。
     */
    fun bindScrollY(signal: Signal<Any?>?) {
        if (signal == null) return
        scrollYBinding = signal
        val ef = Effect {
            val v = signal.get()
            if (v !is Number) return@Effect
            applyingBoundScroll = true
            try {
                scrollY(v.toFloat())
            } finally {
                applyingBoundScroll = false
            }
        }
        lifecycleScope().register(ef)
    }

    /** 绑定 scrollX 到 state 信号(双向);语义同 [bindScrollY]。 */
    fun bindScrollX(signal: Signal<Any?>?) {
        if (signal == null) return
        scrollXBinding = signal
        val ef = Effect {
            val v = signal.get()
            if (v !is Number) return@Effect
            applyingBoundScroll = true
            try {
                scrollX(v.toFloat())
            } finally {
                applyingBoundScroll = false
            }
        }
        lifecycleScope().register(ef)
    }

    // -------- B 类字段：滚动条样式 --------

    fun scrollbarMode(mode: ScrollbarMode?): SELF {
        scrollbarModeField = mode ?: ScrollbarMode.AUTO
        return self()
    }

    fun scrollbarMode(): ScrollbarMode = scrollbarModeField

    fun scrollbarColor(argb: Int): SELF {
        scrollbarColorField = argb
        return self()
    }

    fun scrollbarColor(): Int = scrollbarColorField

    fun scrollbarTrackColor(argb: Int): SELF {
        scrollbarTrackColorField = argb
        return self()
    }

    fun scrollbarTrackColor(): Int = scrollbarTrackColorField

    fun scrollbarWidth(w: Float): SELF {
        scrollbarWidthField = w.coerceAtLeast(0f)
        return self()
    }

    fun scrollbarWidth(): Float = scrollbarWidthField

    // -------- 滚轮步长（逐实例覆盖 SCROLL_STEP） --------

    /** 设置滚轮一次刻度的像素步长；null = 回退默认 [SCROLL_STEP]（20f）。 */
    fun mouseWheelStep(px: Float?): SELF {
        mouseWheelStepField = px
        return self()
    }

    fun mouseWheelStep(): Float? = mouseWheelStepField

    /**
     * 当前生效的滚轮步长：逐实例覆盖优先，未设或非正值回退 [SCROLL_STEP]。
     * 非正值回退可避免 applyScrollEvent 中 `consumed` 除零产生 NaN。
     */
    private fun effectiveWheelStep(): Float {
        val step = mouseWheelStepField
        return if (step != null && step > 0f) step else SCROLL_STEP
    }

    // -------- B 类字段：bounce / inertia / smoothScroll --------

    fun bounce(enabled: Boolean): SELF {
        bounceField = enabled
        return self()
    }

    fun bounce(): Boolean = bounceField

    fun nestedScroll(enabled: Boolean): SELF {
        nestedScrollField = enabled
        return self()
    }

    fun nestedScroll(): Boolean = nestedScrollField

    fun bounceMaxDistance(px: Float): SELF {
        bounceMaxDistanceField = px.coerceAtLeast(0f)
        return self()
    }

    fun bounceMaxDistance(): Float = bounceMaxDistanceField

    fun inertia(enabled: Boolean): SELF {
        inertiaField = enabled
        return self()
    }

    fun inertia(): Boolean = inertiaField

    fun inertiaDecay(decay: Float): SELF {
        // 衰减系数限定在 (0,1)；越界回退默认值。
        inertiaDecayField = if (decay > 0f && decay < 1f) decay else 0.95f
        return self()
    }

    fun inertiaDecay(): Float = inertiaDecayField

    fun smoothScroll(enabled: Boolean): SELF {
        smoothScrollField = enabled
        return self()
    }

    fun smoothScroll(): Boolean = smoothScrollField

    fun smoothScrollDurationMs(ms: Int): SELF {
        smoothScrollDurationMsField = ms.coerceAtLeast(1)
        return self()
    }

    fun smoothScrollDurationMs(): Int = smoothScrollDurationMsField

    // -------- B 类 API：scrollTo / scrollIntoView / position --------

    /** 跳转到 (x,y)。smoothScroll=true 时走平滑动画,否则瞬间跳。 */
    fun scrollTo(x: Float, y: Float): SELF {
        if (smoothScrollField) {
            scrollAnimator.smoothTo(
                scrollXField,
                scrollYField,
                clampX(x),
                clampY(y),
                smoothScrollDurationMsField,
                currentTimeMillis(),
            )
        } else {
            scrollAnimator.stop()
            scrollX(x)
            scrollY(y)
        }
        return self()
    }

    /** 滚到顶部 (0,0)。 */
    fun scrollToTop(): SELF = scrollTo(0f, 0f)

    /** 滚到底部（当前方向的最大值）。 */
    fun scrollToBottom(): SELF = scrollTo(maxScrollX(), maxScrollY())

    /** 是否已滚到底部（容差 1px）。 */
    fun isAtBottom(): Boolean {
        if (directionField == ScrollDirection.HORIZONTAL) {
            return scrollXField >= maxScrollX() - 1f
        }
        return scrollYField >= maxScrollY() - 1f
    }

    /**
     * 滚动到指定 id 子节点。
     * 子节点的 box 需要 LayoutResult,运行时不一定立即可得,故记录为 pending 请求,
     * 由渲染端在下一帧拿到 LayoutResult 后调用 [resolvePendingScrollIntoView] 兑现。
     */
    fun scrollIntoView(childId: String?, align: ViewAlign?): SELF {
        pendingScrollIntoViewIdField = childId
        pendingScrollIntoViewAlignField = align ?: ViewAlign.NEAREST
        return self()
    }

    /** 当前是否有未兑现的 scrollIntoView 请求。 */
    fun pendingScrollIntoViewId(): String? = pendingScrollIntoViewIdField

    fun pendingScrollIntoViewAlign(): ViewAlign = pendingScrollIntoViewAlignField

    /** 清除未兑现的 scrollIntoView 请求（目标子节点不存在时由渲染端调用）。 */
    fun clearPendingScrollIntoView() {
        pendingScrollIntoViewIdField = null
    }

    /**
     * 兑现 scrollIntoView：给定目标子节点相对内容原点的局部坐标 (childLocalX/Y) 与尺寸,
     * 按对齐方式算出目标 scroll 并触发（受 smoothScroll 影响）。由渲染端在拿到 box 后调用。
     */
    fun resolvePendingScrollIntoView(childLocalX: Float, childLocalY: Float, childWidth: Float, childHeight: Float) {
        pendingScrollIntoViewIdField = null
        val targetX = computeAlignedScroll(childLocalX, childWidth, viewportWidthField, scrollXField, maxScrollX())
        val targetY = computeAlignedScroll(childLocalY, childHeight, viewportHeightField, scrollYField, maxScrollY())
        scrollTo(targetX, targetY)
    }

    private fun computeAlignedScroll(
        childPos: Float,
        childSize: Float,
        viewport: Float,
        currentScroll: Float,
        maxScroll: Float,
    ): Float {
        val target = when (pendingScrollIntoViewAlignField) {
            ViewAlign.START -> childPos
            ViewAlign.CENTER -> childPos - (viewport - childSize) / 2f
            ViewAlign.END -> childPos - (viewport - childSize)
            ViewAlign.NEAREST -> nearestScroll(childPos, childSize, viewport, currentScroll)
        }
        return target.coerceIn(0f, maxScroll)
    }

    /** NEAREST：子节点已在视窗内则不动,否则移动最小距离让其完整可见。 */
    private fun nearestScroll(childPos: Float, childSize: Float, viewport: Float, currentScroll: Float): Float {
        val visibleTop = currentScroll
        val visibleBottom = currentScroll + viewport
        if (childPos < visibleTop) {
            return childPos
        }
        if (childPos + childSize > visibleBottom) {
            return childPos + childSize - viewport
        }
        return currentScroll
    }

    /** 滚动位置快照。 */
    fun position(): ScrollPosition {
        val maxX = maxScrollX()
        val maxY = maxScrollY()
        val px = if (maxX <= 0f) 0f else (scrollXField / maxX).coerceIn(0f, 1f)
        val py = if (maxY <= 0f) 0f else (scrollYField / maxY).coerceIn(0f, 1f)
        return ScrollPosition(scrollXField, scrollYField, maxX, maxY, px, py)
    }

    // -------- 帧 tick：推进滚动动画 --------

    /**
     * 由帧调度推进滚动动画（inertia / smoothScroll / bounce-back）。
     *
     * @return 是否仍在动画中
     */
    fun tickScroll(nowMs: Long): Boolean {
        // 自动检测拖拽结束：dragging 标记仍在但已超过一帧无新 drag 事件,
        // 视为松手,触发惯性/回弹（平台层 mouseReleased 不直接通知滚动容器时的兜底）。
        if (dragging && lastDragMs > 0L && (nowMs - lastDragMs) > DRAG_END_IDLE_MS) {
            onDragEnd()
        }
        return scrollAnimator.tick(this, nowMs)
    }

    /** [FrameTickable]：滚动容器每帧推进其滚动动画。返回 true 表示动画活跃（视觉有变化）。 */
    override fun tickFrame(nowMs: Long): Boolean = tickScroll(nowMs)

    /** 动画器内部写入 scroll（经 clamp）；与用户输入路径区分,不打断动画自身。 */
    internal fun applyAnimatedScrollX(x: Float) {
        val old = scrollXField
        scrollXField = clampX(x)
        if (scrollXField != old) onScrollStateChanged()
    }

    internal fun applyAnimatedScrollY(y: Float) {
        val old = scrollYField
        scrollYField = clampY(y)
        if (scrollYField != old) onScrollStateChanged()
    }

    // -------- 事件接管重写：先默认滚动，再回调用户 handler --------

    override fun onScroll(handler: UiEventHandler<ScrollEvent>?): SELF {
        super.onScroll(
            UiEventHandler { e ->
                applyScrollEvent(e)
                handler?.accept(e)
            },
        )
        return self()
    }

    override fun onDrag(handler: UiEventHandler<DragEvent>?): SELF {
        super.onDrag(
            UiEventHandler { e ->
                applyDragEvent(e)
                handler?.accept(e)
            },
        )
        return self()
    }

    // -------- 内部：默认滚动行为 --------

    private fun applyScrollEvent(e: ScrollEvent) {
        // 用户滚轮：打断进行中的动画。
        scrollAnimator.stop()
        // 嵌套滚动协议：基于"剩余量"滚动，并把自己实际消费的量回写到事件。
        // 容器未到边界 → 完整消费 → 不冒泡（行为同改造前）；已到边界 → 消费 0 → 剩余量冒泡到父。
        // 滚轮步长：逐实例 mouseWheelStep 覆盖优先，未设回退 SCROLL_STEP（20f）。
        val wheelStep = effectiveWheelStep()
        if (directionField == ScrollDirection.VERTICAL || directionField == ScrollDirection.BOTH) {
            val remaining = e.remainingY()
            val oldY = scrollYField
            scrollY(scrollYField - (remaining * wheelStep).toFloat())
            val consumed = ((oldY - scrollYField) / wheelStep).toDouble()
            e.consume(0.0, consumed)
        }
        if (directionField == ScrollDirection.HORIZONTAL || directionField == ScrollDirection.BOTH) {
            applyHorizontalWheel(e, wheelStep)
        }
        // 修复"滚轮到边界不回弹":bounce=true 时 clampX/Y 会允许越界 ±bounceMaxDistance,
        // 但回弹只在 onDragEnd 触发,滚轮没有 release 事件 → scroll 永远卡在越界值。
        // 业界标准:滚轮硬切边界,不允许橡皮筋越界(只触摸/拖拽才允许)。
        // 这里在滚轮写入后主动夹回硬边界,保证滚轮路径行为可预期。
        scrollXField = hardClampX(scrollXField)
        scrollYField = hardClampY(scrollYField)
        // nestedScroll=false：吃掉全部剩余量，复刻旧 first-handler-wins（不向父冒泡）。
        if (!nestedScrollField) {
            e.consume(e.remainingX(), e.remainingY())
        }
    }

    /** 单轴平台没有水平滚轮量时，将纵向滚轮映射到纯横向容器。 */
    private fun applyHorizontalWheel(e: ScrollEvent, wheelStep: Float) {
        val useVerticalFallback = directionField == ScrollDirection.HORIZONTAL && e.remainingX() == 0.0
        val remaining = if (useVerticalFallback) e.remainingY() else e.remainingX()
        val oldX = scrollXField
        scrollX(scrollXField - (remaining * wheelStep).toFloat())
        val consumed = ((oldX - scrollXField) / wheelStep).toDouble()
        if (useVerticalFallback) {
            e.consume(0.0, consumed)
        } else {
            e.consume(consumed, 0.0)
        }
    }

    /** 硬钳制 X 到 [0, maxScrollX],不允许 bounce 越界(滚轮路径用)。 */
    private fun hardClampX(x: Float): Float {
        if (directionField == ScrollDirection.VERTICAL) return 0f
        val max = (contentWidth() - viewportWidthField).coerceAtLeast(0f)
        return x.coerceIn(0f, max)
    }

    /** 硬钳制 Y 到 [0, maxScrollY],不允许 bounce 越界(滚轮路径用)。 */
    private fun hardClampY(y: Float): Float {
        if (directionField == ScrollDirection.HORIZONTAL) return 0f
        val max = (contentHeight() - viewportHeightField).coerceAtLeast(0f)
        return y.coerceIn(0f, max)
    }

    private fun applyDragEvent(e: DragEvent) {
        // 用户拖拽：打断进行中的动画,并记录瞬时速度用于松手惯性。
        scrollAnimator.stop()
        val now = currentTimeMillis()
        if (dragging && lastDragMs > 0L) {
            val dtSec = ((now - lastDragMs) / 1000f).coerceAtLeast(0.001f)
            lastDragVelX = e.deltaX.toFloat() / dtSec
            lastDragVelY = e.deltaY.toFloat() / dtSec
        }
        dragging = true
        lastDragMs = now
        if (directionField == ScrollDirection.VERTICAL || directionField == ScrollDirection.BOTH) {
            scrollY(scrollYField + e.deltaY.toFloat())
        }
        if (directionField == ScrollDirection.HORIZONTAL || directionField == ScrollDirection.BOTH) {
            scrollX(scrollXField + e.deltaX.toFloat())
        }
    }

    /**
     * 拖拽结束钩子：松手时按最近速度启动惯性；若内容越界（bounce）则回弹。
     * 由平台层 mouseReleased 或测试显式调用。
     */
    open fun onDragEnd() {
        if (!dragging) {
            maybeBounceBack()
            return
        }
        dragging = false
        val now = currentTimeMillis()
        val recentMove = lastDragMs > 0L && (now - lastDragMs) < 80L
        val overEdge = isOverEdge()
        val hasVelocity = abs(lastDragVelX) > 1f || abs(lastDragVelY) > 1f
        if (overEdge) {
            maybeBounceBack()
        } else if (inertiaField && recentMove && hasVelocity) {
            scrollAnimator.fling(lastDragVelX, lastDragVelY, inertiaDecayField, now)
        }
        lastDragVelX = 0f
        lastDragVelY = 0f
        lastDragMs = -1L
    }

    /** 内容是否越出硬边界（仅 bounce 时可能发生）。 */
    private fun isOverEdge(): Boolean {
        val xOver = scrollXField < 0f || scrollXField > maxScrollX()
        val yOver = scrollYField < 0f || scrollYField > maxScrollY()
        return xOver || yOver
    }

    /** 越界时启动回弹动画拉回最近边界。 */
    private fun maybeBounceBack() {
        if (!bounceField || !isOverEdge()) {
            return
        }
        val tx = scrollXField.coerceIn(0f, maxScrollX())
        val ty = scrollYField.coerceIn(0f, maxScrollY())
        scrollAnimator.bounceBack(scrollXField, scrollYField, tx, ty, currentTimeMillis())
    }

    // -------- 钳制 --------

    /** 水平钳制：垂直容器锁 X=0；否则限制到 [0, contentWidth-viewportWidth]（bounce 时允许越界 bounceMaxDistance）。 */
    protected open fun clampX(x: Float): Float {
        if (directionField == ScrollDirection.VERTICAL) {
            return 0f
        }
        val max = (contentWidth() - viewportWidthField).coerceAtLeast(0f)
        val lo = if (bounceField) -bounceMaxDistanceField else 0f
        val hi = if (bounceField) max + bounceMaxDistanceField else max
        return x.coerceIn(lo, hi)
    }

    /** 垂直钳制：水平容器锁 Y=0；否则限制到 [0, contentHeight-viewportHeight]（bounce 时允许越界 bounceMaxDistance）。 */
    protected open fun clampY(y: Float): Float {
        if (directionField == ScrollDirection.HORIZONTAL) {
            return 0f
        }
        val max = (contentHeight() - viewportHeightField).coerceAtLeast(0f)
        val lo = if (bounceField) -bounceMaxDistanceField else 0f
        val hi = if (bounceField) max + bounceMaxDistanceField else max
        return y.coerceIn(lo, hi)
    }

    /** 当前轴上 scroll 的硬边界最大值（不含 bounce 余量）。 */
    fun maxScrollX(): Float {
        if (directionField == ScrollDirection.VERTICAL) return 0f
        return (contentWidth() - viewportWidthField).coerceAtLeast(0f)
    }

    fun maxScrollY(): Float {
        if (directionField == ScrollDirection.HORIZONTAL) return 0f
        return (contentHeight() - viewportHeightField).coerceAtLeast(0f)
    }

    companion object {
        /** 滚轮一次刻度对应的像素位移（默认值，可被 [mouseWheelStep] 逐实例覆盖）。 */
        private const val SCROLL_STEP = 20f

        /** 拖拽结束自动检测阈值：超过该毫秒无新 drag 事件即视为松手。 */
        private const val DRAG_END_IDLE_MS = 60L
    }
}
